import React from 'react';
import { Modal, Form, Select, Input, Alert, Typography } from 'antd';

const { Option } = Select;
const { Text } = Typography;

/**
 * Motivos de cancelación del catálogo del SAT.
 *
 * Antes las dos rutas de cancelación mandaban '02' fijo sin preguntar, y
 * `UuidSustitucion` nunca se llenaba. El 01 es el único que exige el folio del
 * comprobante que sustituye: mandarlo vacío hace que el SAT rechace la cancelación,
 * y el usuario no tenía forma de proporcionarlo porque nadie se lo pedía.
 */
export const MOTIVOS_CANCELACION = [
  {
    clave: '01',
    titulo: 'Comprobante emitido con errores con relación',
    ayuda: 'La factura tenía errores y ya emitiste otra que la sustituye. El SAT exige el folio fiscal (UUID) de la factura nueva.',
    requiereSustitucion: true,
  },
  {
    clave: '02',
    titulo: 'Comprobante emitido con errores sin relación',
    ayuda: 'La factura tenía errores y no habrá otra que la sustituya.',
    requiereSustitucion: false,
  },
  {
    clave: '03',
    titulo: 'No se llevó a cabo la operación',
    ayuda: 'La venta no ocurrió: el cliente canceló, no se entregó nada.',
    requiereSustitucion: false,
  },
  {
    clave: '04',
    titulo: 'Operación nominativa relacionada en una factura global',
    // El 04 cancela la GLOBAL, no la factura del cliente: un ticket que estaba en la global
    // se va a facturar a nombre del cliente, así que la global se cancela y se reexpide sin
    // él. El texto anterior ("el consumo ya quedó incluido en una global") invitaba a
    // cancelar la nominativa, que es justo al revés.
    ayuda:
      'Solo para cancelar una factura GLOBAL porque un ticket que incluía se facturará a nombre del cliente. ' +
      'La global se reexpide sin ese ticket. Para hacerlo todo en un paso usa "Facturar a nombre del cliente" en la factura global.',
    requiereSustitucion: false,
    soloGlobal: true,
  },
] as const;

export interface CancelarFacturaFormValues {
  motivo: string;
  uuidSustitucion?: string;
}

interface CancelarFacturaModalProps {
  open: boolean;
  folio?: string;
  /** Si la factura es una global. El motivo 04 solo se ofrece para globales. */
  esGlobal?: boolean;
  confirmLoading?: boolean;
  onCancel: () => void;
  /**
   * Si devuelve una promesa, el modal la espera para no dejar pasar un segundo envío
   * mientras el primero sigue en vuelo.
   */
  onConfirm: (values: CancelarFacturaFormValues) => void | Promise<void>;
}

const CancelarFacturaModal: React.FC<CancelarFacturaModalProps> = ({
  open,
  folio,
  esGlobal = false,
  confirmLoading,
  onCancel,
  onConfirm,
}) => {
  const [form] = Form.useForm<CancelarFacturaFormValues>();
  const motivo = Form.useWatch('motivo', form);

  const motivosDisponibles = MOTIVOS_CANCELACION.filter((m) => esGlobal || !('soloGlobal' in m && m.soloGlobal));
  const motivoSeleccionado = motivosDisponibles.find((m) => m.clave === motivo);
  const requiereSustitucion = motivoSeleccionado?.requiereSustitucion ?? false;

  /**
   * Cerrojo contra el doble clic. `confirmLoading` no basta por sí solo: entre el clic y
   * el momento en que el padre pone su bandera de carga en true hay un hueco, y una
   * cancelación duplicada se le manda al SAT de verdad.
   *
   * Se cierra ANTES del primer await. Ponerlo después de `validateFields()` no sirve de
   * nada: las dos ejecuciones leerían false, se quedarían esperando la validación y
   * llamarían las dos a onConfirm — que es justo lo que se quiere evitar. Todo lo que
   * pueda ceder el hilo va dentro del try.
   */
  const enviando = React.useRef(false);

  const handleOk = async () => {
    if (enviando.current) return;
    enviando.current = true;

    try {
      const values = await form.validateFields();

      await onConfirm({
        motivo: values.motivo,
        // Solo el motivo 01 lo lleva; mandarlo en los demás hace que el SAT rechace.
        uuidSustitucion: requiereSustitucion ? values.uuidSustitucion?.trim() : undefined,
      });
    } finally {
      // Se libera pase lo que pase —incluido el fallo de validación—: el modal sigue
      // abierto y el usuario tiene que poder corregir y reintentar.
      enviando.current = false;
    }
  };

  return (
    <Modal
      title="Cancelar factura"
      open={open}
      onCancel={onCancel}
      onOk={handleOk}
      okText="Sí, cancelar"
      okButtonProps={{ danger: true }}
      cancelText="No"
      confirmLoading={confirmLoading}
      afterClose={() => form.resetFields()}
      destroyOnClose
    >
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="Esta acción es irreversible"
        description={
          (folio
            ? `Se solicitará al SAT la cancelación de la factura ${folio}.`
            : 'Se solicitará al SAT la cancelación de esta factura.') +
          ' Si el cliente debe aceptarla, el SAT puede tardar hasta 3 días hábiles en resolverla.'
        }
      />

      <Form form={form} layout="vertical" initialValues={{ motivo: '02' }}>
        <Form.Item
          label="Motivo de cancelación (SAT)"
          name="motivo"
          rules={[{ required: true, message: 'Selecciona el motivo' }]}
          extra={motivoSeleccionado ? <Text type="secondary">{motivoSeleccionado.ayuda}</Text> : undefined}
        >
          <Select>
            {motivosDisponibles.map((m) => (
              <Option key={m.clave} value={m.clave}>
                {m.clave} — {m.titulo}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {requiereSustitucion && (
          <Form.Item
            label="Folio fiscal (UUID) de la factura que la sustituye"
            name="uuidSustitucion"
            rules={[
              { required: true, message: 'El motivo 01 exige el UUID de la factura sustituta' },
              {
                pattern: /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                message: 'Formato de UUID inválido',
              },
            ]}
          >
            <Input placeholder="00000000-0000-0000-0000-000000000000" />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

export default CancelarFacturaModal;
